/**
 * Capability (sub-category specialty) options, with display labels.
 *
 * Mirrors shared/events `CAPABILITIES_BY_CATEGORY` — kept in sync by hand, the
 * same convention `categoryLabels` (opportunities/api.ts) uses, since the web
 * app deliberately does not depend on the @servicegrid/shared-events package.
 * If you add a capability there, add it here too.
 */
export const CAPABILITIES_BY_CATEGORY: Record<string, { value: string; label: string }[]> = {
  plumbing: [
    { value: "water_heater", label: "Water heaters" },
    { value: "drain_sewer", label: "Drain & sewer" },
    { value: "repipe", label: "Repiping" },
    { value: "fixture_install", label: "Fixtures" },
    { value: "gas_line", label: "Gas lines" },
    { value: "sump_pump", label: "Sump pumps" },
  ],
  electrical: [
    { value: "panel_upgrade", label: "Panel upgrades" },
    { value: "ev_charger", label: "EV chargers" },
    { value: "rewiring", label: "Rewiring" },
    { value: "lighting", label: "Lighting" },
    { value: "generator", label: "Generators" },
  ],
  hvac: [
    { value: "ac_repair", label: "AC repair" },
    { value: "furnace", label: "Furnaces" },
    { value: "mini_split", label: "Mini-splits" },
    { value: "duct_work", label: "Ductwork" },
    { value: "boiler", label: "Boilers" },
  ],
  roofing: [
    { value: "shingle", label: "Shingle" },
    { value: "flat_roof", label: "Flat roof" },
    { value: "metal_roof", label: "Metal roof" },
    { value: "gutters", label: "Gutters" },
    { value: "roof_repair", label: "Roof repair" },
  ],
  landscaping: [
    { value: "lawn_care", label: "Lawn care" },
    { value: "tree_service", label: "Tree service" },
    { value: "hardscaping", label: "Hardscaping" },
    { value: "irrigation", label: "Irrigation" },
    { value: "snow_removal", label: "Snow removal" },
  ],
  painting: [
    { value: "interior", label: "Interior" },
    { value: "exterior", label: "Exterior" },
    { value: "cabinet_refinish", label: "Cabinet refinishing" },
    { value: "drywall", label: "Drywall" },
  ],
  cleaning: [
    { value: "deep_clean", label: "Deep cleaning" },
    { value: "carpet", label: "Carpet" },
    { value: "pressure_washing", label: "Pressure washing" },
    { value: "window", label: "Windows" },
    { value: "move_out", label: "Move-out" },
  ],
  appliance_repair: [
    { value: "refrigerator", label: "Refrigerators" },
    { value: "washer_dryer", label: "Washers & dryers" },
    { value: "oven_range", label: "Ovens & ranges" },
    { value: "dishwasher", label: "Dishwashers" },
  ],
  pest_control: [
    { value: "termite", label: "Termites" },
    { value: "rodent", label: "Rodents" },
    { value: "mosquito", label: "Mosquitoes" },
    { value: "bedbug", label: "Bed bugs" },
    { value: "wildlife", label: "Wildlife" },
  ],
};

/** Capability options for the currently-selected categories (deduped, ordered). */
export function capabilityOptionsFor(categories: string[]): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  const seen = new Set<string>();
  for (const cat of categories) {
    for (const opt of CAPABILITIES_BY_CATEGORY[cat] ?? []) {
      if (!seen.has(opt.value)) {
        seen.add(opt.value);
        out.push(opt);
      }
    }
  }
  return out;
}
